import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { message } from 'antd';
import { useTheme } from '../../ThemeContext';
import { getCustomerDetails, submitEnterpriseContact } from '../../utils/apiClient';

const EnterpriseContactModal = ({ open, onClose }) => {
  const { currentTheme, themeMode } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    emailId: '',
    companySize: '',
    monthlyMinutes: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const getAndSetCustomerDetails = async () => {
    const customerDetailsResponse = await getCustomerDetails();
    const customerDetails = customerDetailsResponse.response;
    console.log("Customer details: EnterpriseContactModal", customerDetails);
    setFormData({
      ...formData,
      name: customerDetails.name,
      emailId: customerDetails.email,

    });
  };

  useEffect(() => {
    getAndSetCustomerDetails();
  }, []);

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[\+]?[\d\s\-\(\)]+$/.test(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!formData.emailId.trim()) {
      newErrors.emailId = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailId.trim())) {
      newErrors.emailId = 'Please enter a valid email address';
    }

    if (!formData.companySize.trim()) {
      newErrors.companySize = 'Company size is required';
    }

    if (!formData.monthlyMinutes.trim()) {
      newErrors.monthlyMinutes = 'Monthly minutes requirement is required';
    } else if (isNaN(formData.monthlyMinutes) || parseInt(formData.monthlyMinutes) <= 0) {
      newErrors.monthlyMinutes = 'Please enter a valid number of minutes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await submitEnterpriseContact({
        name: formData.name.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        emailId: formData.emailId.trim(),
        companySize: formData.companySize.trim(),
        monthlyMinutes: parseInt(formData.monthlyMinutes),
        message: formData.message.trim(),
      });

      if (response.statusCode === 'SUCCESS') {
        // Reset form and close modal
        setFormData({
          name: '',
          phoneNumber: '',
          emailId: '',
          companySize: '',
          monthlyMinutes: '',
          message: '',
        });
        setErrors({});
        onClose();
        // Show success message from API response
        message.success(response.message || 'Thank you for your interest! Our team will contact you wthin 24 hours.');
      } else {
        message.error('Failed to submit your request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting enterprise contact form:', error);
      message.error('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        name: '',
        phoneNumber: '',
        emailId: '',
        companySize: '',
        monthlyMinutes: '',
        message: '',
      });
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
          color: currentTheme.homePage?.textColor || (themeMode === 'dark' ? '#ffffff' : '#000000'),
          borderRadius: '16px',
          border: themeMode === 'dark' ? '1px solid #302d4c' : '1px solid #e9e8ea',
          minHeight: '40vh',
          boxShadow: themeMode === 'dark'
            ? '0 20px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.1)'
            : '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
          fontFamily: '"Satoshi", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        },
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" sx={{ color: currentTheme.text.primary, fontWeight: 700, mt:3 }}>
            Enterprise Plan
          </Typography>
          <IconButton
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{ color: currentTheme.text.primary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pb: 3 }}>
        <Box component="form" sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Name Field */}
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ color: currentTheme.text.primary, mb: 1 }}>
                Name*
              </Typography>
              <TextField
                fullWidth
                value={formData.name}
                onChange={handleInputChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                placeholder="Example Hospitals"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: '#3B3757',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FF5C5C',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF5C5C',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: currentTheme.text.primary,
                  },
                }}
              />
            </Grid>

            {/* Phone Number and Email */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ color: currentTheme.text.primary, mb: 1 }}>
                Phone Number*
              </Typography>
              <TextField
                fullWidth
                value={formData.phoneNumber}
                onChange={handleInputChange('phoneNumber')}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                placeholder="+ (620) 635-2826"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: '#3B3757',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FF5C5C',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF5C5C',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: currentTheme.text.primary,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ color: currentTheme.text.primary, mb: 1 }}>
                Email ID*
              </Typography>
              <TextField
                fullWidth
                type="email"
                value={formData.emailId}
                onChange={handleInputChange('emailId')}
                error={!!errors.emailId}
                helperText={errors.emailId}
                placeholder="example@gmail.com"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: '#3B3757',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FF5C5C',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF5C5C',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: currentTheme.text.primary,
                  },
                }}
              />
            </Grid>

            {/* Company Size and Monthly Minutes */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ color: currentTheme.text.primary, mb: 1 }}>
                Company Size*
              </Typography>
              <TextField
                fullWidth
                value={formData.companySize}
                onChange={handleInputChange('companySize')}
                error={!!errors.companySize}
                helperText={errors.companySize}
                placeholder="Enter company size"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: '#3B3757',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FF5C5C',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF5C5C',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: currentTheme.text.primary,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ color: currentTheme.text.primary, mb: 1 }}>
                Monthly Minutes*
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={formData.monthlyMinutes}
                onChange={handleInputChange('monthlyMinutes')}
                error={!!errors.monthlyMinutes}
                helperText={errors.monthlyMinutes}
                placeholder="Required monthly minutes"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: '#3B3757',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FF5C5C',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF5C5C',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: currentTheme.text.primary,
                  },
                }}
              />
            </Grid>

            {/* Message */}
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ color: currentTheme.text.primary, mb: 1 }}>
                Message
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={1}
                value={formData.message}
                onChange={handleInputChange('message')}
                placeholder="Type here..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: '#3B3757',
                    },
                    '&:hover fieldset': {
                      borderColor: '#FF5C5C',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#FF5C5C',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: currentTheme.text.primary,
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{
                borderColor: themeMode === 'dark' ? '#302d4c' : '#666',
                color: themeMode === 'dark' ? '#ffffff' : '#666',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#888',
                  backgroundColor: themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                background: '#FE4E3A',
                color: '#fff',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 600,
                '&:hover': {
                  background: '#FF4444',
                },
                '&:disabled': {
                  background: themeMode === 'dark' ? '#302d4c' : '#999',
                  color: '#cccccc',
                },
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EnterpriseContactModal; 